import React, { useEffect, useRef, useState } from "react";
import { Loader2, LogOut, Pencil, Plus, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { articleService, formatApiError, resolveImageUrl } from "../../services/catalogService";

const inputCls = "w-full h-11 bg-white border border-black/15 px-3 text-sm focus:outline-none focus:border-[#D92A47]";
const areaCls = "w-full bg-white border border-black/15 p-3 text-sm leading-relaxed focus:outline-none focus:border-[#D92A47] resize-y";

const EMPTY = {
  category_en: "", category_ru: "", date: "",
  title_en: "", title_ru: "", excerpt_en: "", excerpt_ru: "",
  body_en: "", body_ru: "",
};

function toForm(a) {
  return {
    category_en: a.categoryEn || "", category_ru: a.categoryRu || "", date: a.date || "",
    title_en: a.titleEn || "", title_ru: a.titleRu || "",
    excerpt_en: a.excerptEn || "", excerpt_ru: a.excerptRu || "",
    body_en: (a.bodyEn || []).join("\n\n"), body_ru: (a.bodyRu || []).join("\n\n"),
  };
}

function toPayload(f) {
  const paras = (s) => s.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
  return {
    category_en: f.category_en, category_ru: f.category_ru, date: f.date,
    title_en: f.title_en, title_ru: f.title_ru,
    excerpt_en: f.excerpt_en, excerpt_ru: f.excerpt_ru,
    body_en: paras(f.body_en), body_ru: paras(f.body_ru),
  };
}

// Task 12 — blog administration: create/edit/delete articles + cover image upload.
export default function BlogPanel({ admin, onLogout }) {
  const [articles, setArticles] = useState(null);
  const [editing, setEditing] = useState(null); // null | "new" | article object
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [confirmId, setConfirmId] = useState(null);
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const reload = () => {
    articleService.list().then(setArticles).catch((e) => toast.error("Could not load articles", { description: formatApiError(e) }));
  };
  useEffect(() => { reload(); }, []);

  const startEdit = (a) => { setEditing(a); setForm(toForm(a)); };
  const startNew = () => { setEditing("new"); setForm(EMPTY); };
  const cancel = () => { setEditing(null); setForm(EMPTY); };

  const save = async () => {
    setBusy(true);
    try {
      if (editing === "new") {
        await articleService.create(toPayload(form));
        toast.success("Article created");
      } else {
        await articleService.update(editing.id, toPayload(form));
        toast.success("Article saved", { description: form.title_en });
      }
      cancel();
      reload();
    } catch (err) {
      toast.error("Could not save", { description: formatApiError(err) });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await articleService.remove(id);
      setConfirmId(null);
      reload();
      toast.success("Article deleted");
    } catch (err) {
      toast.error("Could not delete", { description: formatApiError(err) });
    }
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || editing === "new") return;
    setUploading(true);
    try {
      await articleService.uploadImage(editing.id, file);
      reload();
      setEditing((prev) => ({ ...prev }));
      toast.success("Cover image updated");
    } catch (err) {
      toast.error("Upload failed", { description: formatApiError(err) });
    } finally {
      setUploading(false);
    }
  };

  const F = (key, label, opts = {}) => (
    <label className="block" key={key}>
      <span className="block text-[11px] uppercase tracking-[0.14em] text-[#817B74] mb-1">{label}</span>
      {opts.area ? (
        <textarea rows={opts.rows || 4} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })}
          data-testid={`article-field-${key}`} className={areaCls} placeholder={opts.ph} />
      ) : (
        <input value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })}
          data-testid={`article-field-${key}`} className={inputCls} placeholder={opts.ph} />
      )}
    </label>
  );

  return (
    <div className="max-w-4xl mx-auto" data-testid="admin-blog-panel">
      <div className="flex items-center justify-between mb-10 gap-4 flex-wrap">
        <div>
          <span className="font-sans text-xs uppercase tracking-[0.24em] text-[#D92A47]">Admin</span>
          <h1 className="font-serif text-4xl md:text-5xl text-[#252525] mt-2">Blog</h1>
          <p className="text-[#817B74] text-sm mt-1">
            Articles shown in Floral News and /blog. Signed in as {admin.email}
          </p>
        </div>
        <button onClick={onLogout} data-testid="admin-logout"
          className="inline-flex items-center gap-2 h-10 px-4 border border-black/15 text-[11px] uppercase tracking-[0.14em] hover:border-[#D92A47] hover:text-[#D92A47]">
          <LogOut size={14} /> Sign out
        </button>
      </div>

      {articles === null ? (
        <div className="text-[#817B74] py-20 text-center"><Loader2 className="animate-spin inline mr-2" size={16} />Loading…</div>
      ) : editing === null ? (
        <>
          <button onClick={startNew} data-testid="article-new"
            className="mb-6 inline-flex items-center gap-2 h-11 px-6 bg-[#D92A47] text-[#F6F2EC] text-[12px] font-semibold uppercase tracking-[0.14em] hover:bg-[#b71e3a]">
            <Plus size={14} /> New article
          </button>
          <div className="space-y-4">
            {articles.map((a) => (
              <div key={a.id} className="bg-white border border-black/10 p-4 flex items-center gap-4" data-testid={`article-row-${a.id}`}>
                <div className="h-14 w-20 bg-[#EDE8E0] overflow-hidden shrink-0">
                  {a.image && <img src={resolveImageUrl(a.image)} alt="" className="h-full w-full object-cover" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-serif text-lg text-[#252525] truncate">{a.titleEn}</p>
                  <p className="text-[11px] uppercase tracking-[0.12em] text-[#817B74]">{a.categoryEn} · {a.date}</p>
                </div>
                <button onClick={() => startEdit(a)} data-testid={`article-edit-${a.id}`} aria-label={`Edit ${a.titleEn}`}
                  className="grid h-10 w-10 place-items-center border border-black/15 text-[#817B74] hover:border-[#D92A47] hover:text-[#D92A47]">
                  <Pencil size={15} />
                </button>
                {confirmId === a.id ? (
                  <button onClick={() => remove(a.id)} data-testid={`article-delete-confirm-${a.id}`}
                    className="h-10 px-4 bg-[#D92A47] text-white text-[11px] font-semibold uppercase tracking-[0.12em]">
                    Confirm delete
                  </button>
                ) : (
                  <button onClick={() => setConfirmId(a.id)} data-testid={`article-delete-${a.id}`} aria-label={`Delete ${a.titleEn}`}
                    className="grid h-10 w-10 place-items-center border border-black/15 text-[#817B74] hover:border-[#D92A47] hover:text-[#D92A47]">
                    <Trash2 size={15} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="bg-white border border-black/10 p-6 md:p-8" data-testid="article-form">
          <h2 className="font-serif text-2xl mb-6">{editing === "new" ? "New article" : `Edit: ${editing.titleEn}`}</h2>

          {editing !== "new" && (
            <div className="mb-6 flex items-center gap-4">
              <div className="h-20 w-32 bg-[#EDE8E0] overflow-hidden border border-black/10">
                {editing.image && <img src={resolveImageUrl(editing.image)} alt="" className="h-full w-full object-cover" data-testid="article-image-preview" />}
              </div>
              <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="article-image-upload"
                className="inline-flex items-center gap-2 h-10 px-4 border border-black/15 text-[11px] uppercase tracking-[0.12em] hover:border-[#D92A47] hover:text-[#D92A47] disabled:opacity-60">
                {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                {uploading ? "Uploading…" : "Replace cover"}
              </button>
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden"
                onChange={uploadImage} data-testid="article-image-input" />
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {F("category_en", "Category (EN)")}
            {F("category_ru", "Category (RU)")}
            {F("date", "Date label", { ph: "e.g. Feb 2026" })}
          </div>
          <div className="grid grid-cols-1 gap-4 mt-4">
            {F("title_en", "Title (EN) — required")}
            {F("title_ru", "Title (RU)")}
            {F("excerpt_en", "Excerpt (EN)", { area: true, rows: 2 })}
            {F("excerpt_ru", "Excerpt (RU)", { area: true, rows: 2 })}
            {F("body_en", "Body (EN) — blank line between paragraphs", { area: true, rows: 6 })}
            {F("body_ru", "Body (RU) — blank line between paragraphs", { area: true, rows: 6 })}
          </div>

          <div className="flex gap-3 mt-6">
            <button onClick={save} disabled={busy || !form.title_en.trim()} data-testid="article-save"
              className="inline-flex items-center gap-2 h-11 px-6 bg-[#D92A47] text-[#F6F2EC] text-[12px] font-semibold uppercase tracking-[0.14em] hover:bg-[#b71e3a] disabled:opacity-50">
              {busy ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : editing === "new" ? "Create article" : "Save article"}
            </button>
            <button onClick={cancel} data-testid="article-cancel"
              className="h-11 px-5 border border-black/15 text-[12px] uppercase tracking-[0.14em] hover:border-[#D92A47]">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
